import { type ReactNode } from 'react'
import { Link, useLocation } from 'react-router'
import { ChevronRight } from 'lucide-react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { Badge } from '../ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  type NavCollapsible,
  type NavItem,
  type NavLink,
  type NavGroup as NavGroupProps,
} from './types'

export function NavGroup({ title, items }: NavGroupProps) {
  const { state, isMobile } = useSidebar()
  const location = useLocation()
  const href = location.pathname + location.search
  return (
    <SidebarGroup>
      {title ? <SidebarGroupLabel>{title}</SidebarGroupLabel> : null}
      <SidebarMenu>
        {items.map((item) => {
          const key = `${item.title}-${item.url}`

          if (!item.items) {
            return <SidebarMenuLink key={key} item={item} href={href} />
          }

          if (state === 'collapsed' && !isMobile) {
            return (
              <SidebarMenuCollapsedDropdown key={key} item={item} href={href} />
            )
          }

          return <SidebarMenuCollapsible key={key} item={item} href={href} />
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}

function NavBadge({ children }: { children: ReactNode }) {
  return <Badge className='rounded-full px-1 py-0 text-xs'>{children}</Badge>
}

function NavComingSoonBadge({ children }: { children: ReactNode }) {
  return (
    <Badge
      variant='outline'
      className='ms-auto shrink-0 opacity-100 border-violet-200 bg-violet-50 font-normal text-violet-800 group-data-[collapsible=icon]:hidden dark:border-violet-800/50 dark:bg-violet-950/40 dark:text-violet-200'
    >
      {children}
    </Badge>
  )
}

function SidebarMenuLinkContent({ item }: { item: NavLink }) {
  const mutedClass = item.disabled ? 'opacity-50' : undefined

  return (
    <>
      {item.icon ? <item.icon className={mutedClass} /> : null}
      <span className={mutedClass}>{item.title}</span>
      {item.badge ? <NavBadge>{item.badge}</NavBadge> : null}
      {item.comingSoon ? (
        <NavComingSoonBadge>{item.comingSoon.badge}</NavComingSoonBadge>
      ) : null}
    </>
  )
}

function SidebarMenuLink({ item, href }: { item: NavLink; href: string }) {
  const { setOpenMobile } = useSidebar()
  const tooltip = item.comingSoon?.tooltip ?? item.title

  if (item.disabled) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          isActive={false}
          tooltip={tooltip}
          className='cursor-not-allowed hover:bg-transparent hover:text-sidebar-foreground active:bg-transparent'
          onClick={(event) => {
            event.preventDefault()
          }}
        >
          <SidebarMenuLinkContent item={item} />
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={checkIsActive(href, item)}
        tooltip={tooltip}
      >
        {item.external ? (
          <a href={item.url} onClick={() => setOpenMobile(false)}>
            <SidebarMenuLinkContent item={item} />
          </a>
        ) : (
          <Link to={item.url} onClick={() => setOpenMobile(false)}>
            <SidebarMenuLinkContent item={item} />
          </Link>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

function SidebarMenuCollapsible({
  item,
  href,
}: {
  item: NavCollapsible
  href: string
}) {
  const { setOpenMobile } = useSidebar()
  return (
    <Collapsible
      defaultOpen={checkIsActive(href, item)}
      className='group/collapsible'
      render={<SidebarMenuItem />}
    >
        <CollapsibleTrigger
          render={
            <SidebarMenuButton tooltip={item.title} />
          }
        >
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            {item.badge && <NavBadge>{item.badge}</NavBadge>}
            <ChevronRight className='ms-auto transition-transform duration-200 in-data-panel-open:rotate-90 rtl:rotate-180' />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.items.map((subItem) => (
              <SidebarMenuSubItem key={subItem.title}>
                <SidebarMenuSubButton
                  asChild
                  isActive={checkIsActive(href, subItem)}
                >
                  <Link
                    to={subItem.url}
                    state={subItem.state}
                    onClick={() => setOpenMobile(false)}
                  >
                    {subItem.icon && <subItem.icon />}
                    <span>{subItem.title}</span>
                    {subItem.badge && <NavBadge>{subItem.badge}</NavBadge>}
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
    </Collapsible>
  )
}

function SidebarMenuCollapsedDropdown({
  item,
  href,
}: {
  item: NavCollapsible
  href: string
}) {
  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            isActive={checkIsActive(href, item)}
          >
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            {item.badge && <NavBadge>{item.badge}</NavBadge>}
            <ChevronRight className='ms-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent side='right' align='start' sideOffset={4}>
          <DropdownMenuLabel>
            {item.title} {item.badge ? `(${item.badge})` : ''}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {item.items.map((sub) => (
            <DropdownMenuItem key={`${sub.title}-${sub.url}`} asChild>
              <Link
                to={sub.url}
                state={sub.state}
                className={`${checkIsActive(href, sub) ? 'bg-secondary' : ''}`}
              >
                {sub.icon && <sub.icon />}
                <span className='max-w-52 text-wrap'>{sub.title}</span>
                {sub.badge && (
                  <span className='ms-auto text-xs'>{sub.badge}</span>
                )}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  )
}

/** True when `pathname` is the nav entry or a nested route under it (not a sibling path). */
function pathnameMatchesNavUrl(pathname: string, navUrl: string): boolean {
  if (navUrl === '/' || navUrl === '') {
    return pathname === '/' || pathname === ''
  }
  return pathname === navUrl || pathname.startsWith(`${navUrl}/`)
}

function checkIsActive(href: string, item: NavItem) {
  if ('url' in item && item.url != null && href === item.url) {
    return true
  }

  const pathname = href.split('?')[0] || '/'
  const matchesUrl = (navUrl: string) => pathnameMatchesNavUrl(pathname, navUrl)

  if ('items' in item && item.items) {
    if (item.items.some((child) => matchesUrl(child.url))) {
      return true
    }
  }

  if ('url' in item && item.url != null && matchesUrl(item.url)) {
    return true
  }

  return false
}
